// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AdminRosterModal } from "@/components/admin/AdminRosterModal";
import { EventPacketManager } from "@/components/admin/EventPacketManager";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Clock,
  Eye,
  FileText,
  Search,
  Users,
} from "lucide-react";

interface TeamRoster {
  id: string; // registration id
  team_id: string; // may be null in DB; we fall back to registration id
  team_name: string;
  coach_name: string | null;
  coach_email: string | null;
  coach_phone: string | null;
  roster_status: "submitted" | "pending" | "not_started";
  roster_count: number;
  submitted_at: string | null;
  division_name: string | null;
}

interface EventInfo {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
}

const EventRostersAdmin: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  const [event, setEvent] = useState<EventInfo | null>(null);
  const [rosters, setRosters] = useState<TeamRoster[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "submitted" | "pending" | "not_started"
  >("all");

  const [selectedTeam, setSelectedTeam] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [isRosterModalOpen, setIsRosterModalOpen] = useState(false);

  const openRosterModal = (teamId: string, teamName: string) => {
    setSelectedTeam({ id: teamId, name: teamName });
    setIsRosterModalOpen(true);
  };

  useEffect(() => {
    if (eventId) fetchEventAndRosters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchEventAndRosters = async () => {
    if (!eventId) return;

    setIsLoading(true);
    try {
      // 1) Event info
      const { data: eventData, error: eventError } = await supabase
        .from("events")
        .select("id, name, start_date, end_date")
        .eq("id", eventId)
        .single();

      if (eventError) throw eventError;
      setEvent(eventData);

      // 2) Get team registrations for this event
      const { data: registrations, error: regsError } = await supabase
        .from("registrations")
        .select("id, team_id, roster_submitted_at, registrant_data")
        .eq("event_id", eventId)
        .eq("registration_type", "team");

      if (regsError) throw regsError;

      // 3) Map JSONB registrant_data into the roster table
      const rosterData: TeamRoster[] = (registrations || []).map((reg: any) => {
        const registrantData = (reg.registrant_data || {}) as any;
        const team = (registrantData.team || {}) as any;
        const coach = (registrantData.coach || {}) as any;

        const submittedAt = (reg.roster_submitted_at || null) as string | null;

        return {
          id: reg.id,
          team_id: (reg.team_id || reg.id) as string,
          team_name: (team.name || "Unknown Team") as string,
          coach_name: (coach.name || null) as string | null,
          coach_email: (coach.email || null) as string | null,
          coach_phone: (coach.phone || null) as string | null,
          roster_status: submittedAt ? "submitted" : "not_started",
          roster_count: 0,
          submitted_at: submittedAt,
          division_name: (team.division_id || null) as string | null,
        };
      });

      setRosters(rosterData);
    } catch (error) {
      console.error("Error fetching rosters:", error);
      setRosters([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredRosters = rosters.filter((roster) => {
    const matchesSearch =
      roster.team_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (roster.coach_name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);

    const matchesStatus = statusFilter === "all" || roster.roster_status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: rosters.length,
    submitted: rosters.filter((r) => r.roster_status === "submitted").length,
    pending: rosters.filter((r) => r.roster_status === "pending").length,
    notStarted: rosters.filter((r) => r.roster_status === "not_started").length,
  };

  const getStatusBadge = (status: TeamRoster["roster_status"]) => {
    switch (status) {
      case "submitted":
        return (
          <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
            <CheckCircle className="h-3 w-3 mr-1" />
            Submitted
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">
            <Clock className="h-3 w-3 mr-1" />
            In Progress
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-muted-foreground">
            <AlertCircle className="h-3 w-3 mr-1" />
            Not Started
          </Badge>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{event?.name || "Event"} - Rosters</h1>
            <p className="text-muted-foreground">Manage team rosters and packet generation</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="rosters" className="space-y-4">
        <TabsList>
          <TabsTrigger value="rosters">
            <Users className="h-4 w-4 mr-2" />
            Team Rosters
          </TabsTrigger>
          <TabsTrigger value="packets">
            <FileText className="h-4 w-4 mr-2" />
            Packets
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rosters" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{stats.total}</div>
                <p className="text-sm text-muted-foreground">Total Teams</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-emerald-600">{stats.submitted}</div>
                <p className="text-sm text-muted-foreground">Submitted</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
                <p className="text-sm text-muted-foreground">In Progress</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-muted-foreground">{stats.notStarted}</div>
                <p className="text-sm text-muted-foreground">Not Started</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search teams or coaches..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant={statusFilter === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStatusFilter("all")}
                  >
                    All
                  </Button>
                  <Button
                    variant={statusFilter === "submitted" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStatusFilter("submitted")}
                  >
                    Submitted
                  </Button>
                  <Button
                    variant={statusFilter === "pending" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStatusFilter("pending")}
                  >
                    In Progress
                  </Button>
                  <Button
                    variant={statusFilter === "not_started" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStatusFilter("not_started")}
                  >
                    Not Started
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Team Name</TableHead>
                    <TableHead>Coach</TableHead>
                    <TableHead>Division</TableHead>
                    <TableHead>Players</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRosters.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No rosters found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRosters.map((roster) => (
                      <TableRow key={roster.id}>
                        <TableCell className="font-medium">{roster.team_name}</TableCell>
                        <TableCell>
                          <div>
                            {roster.coach_name || "No coach assigned"}
                            {roster.coach_email && (
                              <div className="text-xs text-muted-foreground">{roster.coach_email}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{roster.division_name || "—"}</TableCell>
                        <TableCell>{roster.roster_count > 0 ? roster.roster_count : "—"}</TableCell>
                        <TableCell>{getStatusBadge(roster.roster_status)}</TableCell>
                        <TableCell>
                          {roster.submitted_at
                            ? new Date(roster.submitted_at).toLocaleDateString()
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openRosterModal(roster.team_id, roster.team_name)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="packets" className="space-y-4">
          {eventId && event && <EventPacketManager eventId={eventId} eventName={event.name} />}
        </TabsContent>
      </Tabs>

      {eventId && selectedTeam && (
        <AdminRosterModal
          open={isRosterModalOpen}
          onOpenChange={setIsRosterModalOpen}
          eventId={eventId}
          teamId={selectedTeam.id}
          teamName={selectedTeam.name}
          onRosterUpdated={fetchEventAndRosters}
        />
      )}
    </div>
  );
};

export default EventRostersAdmin;
